import { Divider, PageFooter, Text, Title } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { MissedBadgeCounter } from "./MissedBadgeCounter";

interface WorkBadgeReviewPolicyRedesignProps {
  onClose: () => void;
  onNeedNewBadge: () => void;
  unverifiedBadgeCount: number;
  hasWorkedBadgeRequiredShift: boolean;
}

const BADGE_VERIFICATION_STEPS = [
  {
    number: 1,
    title: "Order a badge",
    description: "You can order a badge from your account page. Already have one? Perfect!",
  },
  {
    number: 2,
    title: "Forgot your badge? Get a temporary one",
    description: "Head to the workplace front desk to request a temporary badge, if available.",
  },
  {
    number: 3,
    title: "Wear your badge on shifts",
    description:
      "Make sure your full name and shift role are on the badge, and wear it over your work attire.",
  },
  {
    number: 4,
    title: "Verify your badge",
    description: "Take a high-quality selfie with your full badge on your work attire and upload!",
  },
] as const;

export function WorkBadgeReviewPolicyRedesign(props: WorkBadgeReviewPolicyRedesignProps) {
  const { onClose, onNeedNewBadge, unverifiedBadgeCount, hasWorkedBadgeRequiredShift } = props;

  return (
    <>
      <StickyScrollPageHeader title="" headerVariant="tertiary" onClickBack={onClose} />

      <PageContent>
        <Stack spacing={8} sx={{ paddingTop: 4 }}>
          <Stack spacing={3}>
            <Title component="h3">Wear your badge</Title>
            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
              Wearing a badge builds trust and helps residents feel safe It shows staff and
              residents who you are
            </Text>
          </Stack>

          <MissedBadgeCounter
            unverifiedBadgeCount={unverifiedBadgeCount}
            hasWorkedBadgeRequiredShift={hasWorkedBadgeRequiredShift}
          />

          <Divider />

          <Stack spacing={5}>
            <Title
              component="h2"
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              Badge Policy
            </Title>
            <Stack spacing={5}>
              <Text variant="body2">Wear your badge to workplaces that require one.</Text>
              <Text variant="body2">
                If you miss badge verification 3 times in 60 days, you will be restricted from
                badge-required shifts for 30 days.
              </Text>
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={4}>
            <Title
              component="h2"
              variant="h5"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              How to get badge verified
            </Title>
            <Stack spacing={8}>
              {BADGE_VERIFICATION_STEPS.map((step) => (
                <Stack key={step.number} direction="row" spacing={3} alignItems="flex-start">
                  <Text bold variant="body1" sx={{ minWidth: "1.25rem", textAlign: "center" }}>
                    {step.number}
                  </Text>
                  <Stack spacing={2}>
                    <Text bold variant="body1">
                      {step.title}
                    </Text>
                    <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                      {step.description}
                    </Text>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </PageContent>

      <PageFooter>
        <Stack alignItems="center" spacing={4} width="100%">
          <Button
            fullWidth
            type="submit"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.WORK_BADGE_NEED_NEW_BADGE_CLICKED);
              onNeedNewBadge();
            }}
          >
            Get a badge
          </Button>
        </Stack>
      </PageFooter>
    </>
  );
}
